import React, { useEffect, useState } from 'react';
import { masterSettingsAPI } from '@/api/api';
import { Plus, Edit, Trash2, Search, DollarSign, Filter, TrendingUp, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const FeeStructures = () => {
  const [feeStructures, setFeeStructures] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);  // Store all sessions
  const [filteredSessions, setFilteredSessions] = useState([]);  // Filtered by course
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentFee, setCurrentFee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    course_id: '',
    total_semesters: 0,
    session_id: '',
    regular_fee: '',
    lateral_fee: '',
    caution_money: '',
    has_caution_money: false,
    fee_type: 'Course Fees'
  });

  const feeTypes = [
    'Course Fees'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [feesRes, coursesRes, sessionsRes, semestersRes] = await Promise.all([
        masterSettingsAPI.getFeeStructures().catch((err) => { console.error('Fee structures fetch failed:', err); return { data: [] }; }),
        masterSettingsAPI.getCourses().catch((err) => { console.error('Courses fetch failed:', err); return { data: [] }; }),
        masterSettingsAPI.getSessions().catch((err) => { console.error('Sessions fetch failed:', err); return { data: [] }; }),
        masterSettingsAPI.getSemesters().catch((err) => { console.error('Semesters fetch failed:', err); return { data: [] }; })
      ]);
      console.log('Data fetched:', { fees: feesRes.data.length, courses: coursesRes.data.length, sessions: sessionsRes.data.length, semesters: semestersRes.data.length });
      setFeeStructures(feesRes.data || []);
      setCourses(coursesRes.data || []);
      setAllSessions(sessionsRes.data || []);  // Store all sessions
      setSessions(sessionsRes.data || []);
      setFilteredSessions([]);  // Start empty until course selected
      setSemesters(semestersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = async (courseId) => {
    console.log('Course changed to:', courseId);
    
    // Filter sessions by selected course
    const filtered = allSessions.filter(session => {
      // Check if session has course_id (new sessions)
      if (session.course_id === courseId) return true;
      
      // Check if session has course_ids array (old sessions)
      if (session.course_ids && session.course_ids.includes(courseId)) return true;
      
      return false;
    });
    
    console.log(`Filtered ${filtered.length} sessions for course ${courseId}`);
    setFilteredSessions(filtered);
    
    // Fetch semesters for the selected course
    try {
      const semestersRes = await masterSettingsAPI.getSemesters(courseId);
      const totalSemesters = semestersRes.data.length;
      console.log(`Found ${totalSemesters} semesters for course ${courseId}`);
      
      setFormData({
        ...formData,
        course_id: courseId,
        session_id: '',  // Reset session when course changes
        total_semesters: totalSemesters
      });
    } catch (error) {
      console.error('Error fetching semesters:', error);
      setFormData({
        ...formData,
        course_id: courseId,
        session_id: '',
        total_semesters: 0
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that course has semesters
    if (!editMode && formData.total_semesters === 0) {
      toast.error('Cannot create fee structure: No semesters found for this course. Please create semesters first in Master Settings → Semesters.');
      return;
    }
    
    try {
      // Auto-generate name from course and session
      const course = courses.find(c => c.id === formData.course_id);
      const session = sessions.find(s => s.id === formData.session_id);
      const courseName = course ? course.name : 'Unknown Course';
      const sessionName = session ? session.name : '';
      const autoName = `${courseName} - ${formData.fee_type}${sessionName ? ` (${sessionName})` : ''}`;
      
      const submitData = {
        ...formData,
        name: autoName,
        total_semesters: parseInt(formData.total_semesters),
        regular_fee: parseFloat(formData.regular_fee || 0),
        lateral_fee: course?.lateral_entry_allowed ? parseFloat(formData.lateral_fee || 0) : parseFloat(formData.regular_fee || 0),
        total_fee: parseFloat(formData.regular_fee || 0), // Keep for backward compatibility
        caution_money: formData.has_caution_money ? parseFloat(formData.caution_money || 0) : 0
      };
      
      let response;
      if (editMode) {
        response = await masterSettingsAPI.updateFeeStructure(currentFee.id, submitData);
        
        // Check for fee locking warning from backend
        if (response.data?.warning) {
          toast.warning(response.data.warning, {
            duration: 8000,
            icon: <AlertTriangle className="h-5 w-5 text-amber-500" />
          });
        }
        toast.success('Fee structure updated successfully');
      } else {
        response = await masterSettingsAPI.createFeeStructure(submitData);
        toast.success('Fee structure created successfully');
      }
      setIsDialogOpen(false);
      setEditMode(false);
      setCurrentFee(null);
      setFormData({ name: '', course_id: '', total_semesters: 0, session_id: '', regular_fee: '', lateral_fee: '', caution_money: '', has_caution_money: false, fee_type: 'Course Fees' });
      fetchData();
    } catch (error) {
      console.error('Error saving fee structure:', error);
      toast.error('Failed to save fee structure');
    }
  };

  const handleEdit = (fee) => {
    setEditMode(true);
    setFormData({
      name: fee.name,
      course_id: fee.course_id,
      total_semesters: fee.total_semesters || 1,
      session_id: fee.session_id || '',
      regular_fee: fee.regular_fee?.toString() || fee.total_fee?.toString() || '',
      lateral_fee: fee.lateral_fee?.toString() || fee.total_fee?.toString() || '',
      caution_money: fee.caution_money?.toString() || '',
      has_caution_money: fee.has_caution_money || false,
      fee_type: fee.fee_type
    });
    setCurrentFee(fee);
    
    // Filter sessions based on the course from fee being edited
    if (fee.course_id) {
      const filtered = allSessions.filter(session => {
        if (session.course_id === fee.course_id) return true;
        if (session.course_ids && session.course_ids.includes(fee.course_id)) return true;
        return false;
      });
      console.log(`[EDIT] Filtered ${filtered.length} sessions for course ${fee.course_id}`);
      setFilteredSessions(filtered);
    }
    
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this fee structure?')) {
      try {
        await masterSettingsAPI.deleteFeeStructure(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting fee structure:', error);
        alert('Failed to delete fee structure');
      }
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'Unknown';
  };

  const getCourseCode = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.code : '-';
  };

  const getSessionName = (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    return session ? session.name : '-';
  };

  const getCourseWiseSemesterCount = (courseId, isLateralEntry = false) => {
    if (!courseId) return 0;
    const courseSemesters = semesters.filter(s => s.course_id === courseId);
    
    if (isLateralEntry) {
      // For lateral entry, count only semesters with entry_type = "lateral" or "both"
      return courseSemesters.filter(s => s.entry_type === 'lateral' || s.entry_type === 'both').length;
    }
    
    // For regular, count all semesters
    return courseSemesters.length;
  };

  // Auto-calculate total_semesters when course or lateral entry status is selected
  useEffect(() => {
    if (formData.course_id && !editMode) {
      const semesterCount = getCourseWiseSemesterCount(formData.course_id, formData.is_lateral_entry);
      setFormData(prev => ({ ...prev, total_semesters: semesterCount }));
    }
  }, [formData.course_id, formData.is_lateral_entry]);

  const filteredFeeStructures = feeStructures.filter(fee => {
    const matchesSearch = fee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fee.fee_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCourse = selectedCourseFilter === '' || fee.course_id === selectedCourseFilter;
    const matchesType = selectedTypeFilter === '' || fee.fee_type === selectedTypeFilter;
    return matchesSearch && matchesCourse && matchesType;
  });

  // Group by fee type
  const groupedFees = feeTypes.map(type => {
    const typeFees = filteredFeeStructures.filter(f => f.fee_type === type);
    return {
      type,
      fees: typeFees,
      count: typeFees.length,
      totalAmount: typeFees.reduce((sum, f) => sum + (f.total_fee || 0), 0)
    };
  }).filter(group => group.count > 0);

  // Calculate statistics
  const totalFees = feeStructures.length;
  const totalAmount = feeStructures.reduce((sum, f) => sum + (f.total_fee || 0), 0);
  const avgAmount = totalFees > 0 ? totalAmount / totalFees : 0;
  const uniqueCourses = new Set(feeStructures.map(f => f.course_id)).size;

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="loading">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="fee-structures-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Fee Structures Management</h1>
          <p className="text-gray-600 mt-1">Define fee structures for courses and semesters</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditMode(false);
            setCurrentFee(null);
            setFormData({ name: '', course_id: '', total_semesters: 0, session_id: '', regular_fee: '', lateral_fee: '', caution_money: '', has_caution_money: false, fee_type: 'Course Fees' });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-fee-button">
              <Plus size={20} className="mr-2" /> Add Fee Structure
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="fee-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Fee Structure' : 'Add New Fee Structure'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 max-h-[80vh] overflow-y-auto">
              {/* Row 1: Course and Session */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="course_id">Course *</Label>
                  <select
                    id="course_id"
                    className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm"
                    value={formData.course_id}
                    onChange={(e) => handleCourseChange(e.target.value)}
                    required
                    data-testid="course-select"
                  >
                    <option value="">Select Course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="session_id">Session * {formData.course_id && `(${filteredSessions.length} found)`}</Label>
                  <select
                    id="session_id"
                    className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm"
                    value={formData.session_id}
                    onChange={(e) => setFormData({ ...formData, session_id: e.target.value })}
                    required
                    disabled={!formData.course_id}
                    data-testid="session-select"
                  >
                    <option value="">{!formData.course_id ? 'Select Course First' : 'Select Session'}</option>
                    {filteredSessions.map(session => (
                      <option key={session.id} value={session.id}>
                        {session.name} {session.is_current ? '(Current)' : ''}
                      </option>
                    ))}
                  </select>
                  {formData.course_id && filteredSessions.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      ⚠️ No sessions found. Create a session for this course in Master Settings → Sessions
                    </p>
                  )}
                  {formData.course_id && filteredSessions.length > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ {filteredSessions.length} session(s) available
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2: Fee Type */}
              <div>
                <Label htmlFor="fee_type">Fee Type *</Label>
                <select
                  id="fee_type"
                  className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm"
                  value={formData.fee_type}
                  onChange={(e) => setFormData({ ...formData, fee_type: e.target.value })}
                  required
                  data-testid="fee-type-select"
                >
                  {feeTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Row 3: Regular Fee and Lateral Fee */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="regular_fee">Regular Entry Fee (₹) *</Label>
                  <Input
                    id="regular_fee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.regular_fee}
                    onChange={(e) => setFormData({ ...formData, regular_fee: e.target.value })}
                    placeholder="For 1st Semester students"
                    required
                    className="text-sm"
                    data-testid="regular-fee-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">For students joining from 1st Semester</p>
                </div>

                {(() => {
                  const selectedCourse = courses.find(c => c.id === formData.course_id);
                  if (selectedCourse?.lateral_entry_allowed) {
                    return (
                      <div>
                        <Label htmlFor="lateral_fee">Lateral Entry Fee (₹) *</Label>
                        <Input
                          id="lateral_fee"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.lateral_fee}
                          onChange={(e) => setFormData({ ...formData, lateral_fee: e.target.value })}
                          placeholder={`For ${selectedCourse?.lateral_entry_semester || 3}rd Semester students`}
                          required
                          className="text-sm"
                          data-testid="lateral-fee-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          For students joining from {selectedCourse?.lateral_entry_semester || 3}rd Semester
                        </p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-400">Lateral Entry not available for this course</p>
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Row 4: Caution Money */}
              <div className="flex items-center gap-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="has_caution_money"
                    checked={formData.has_caution_money}
                    onChange={(e) => setFormData({ ...formData, has_caution_money: e.target.checked, caution_money: e.target.checked ? formData.caution_money : '' })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="has_caution_money" className="text-sm font-normal cursor-pointer">
                    Caution Money
                  </Label>
                </div>
                
                {formData.has_caution_money && (
                  <div className="flex-1">
                    <Input
                      id="caution_money"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.caution_money}
                      onChange={(e) => setFormData({ ...formData, caution_money: e.target.value })}
                      placeholder="Amount"
                      required={formData.has_caution_money}
                      className="text-sm"
                      data-testid="caution-money-input"
                    />
                  </div>
                )}
              </div>

              {/* Preview Section - Compact */}
              {formData.course_id && formData.session_id && formData.regular_fee && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-semibold text-blue-800 text-sm mb-1.5">Preview</h4>
                  <div className="text-xs text-blue-700 grid grid-cols-2 gap-x-3 gap-y-1">
                    <div><span className="font-medium">Course:</span> {courses.find(c => c.id === formData.course_id)?.name || 'N/A'}</div>
                    <div><span className="font-medium">Session:</span> {sessions.find(s => s.id === formData.session_id)?.name || 'N/A'}</div>
                    <div><span className="font-medium">Type:</span> {formData.fee_type}</div>
                    <div><span className="font-medium">Regular Fee:</span> ₹{parseFloat(formData.regular_fee || 0).toLocaleString('en-IN')}</div>
                    {courses.find(c => c.id === formData.course_id)?.lateral_entry_allowed && (
                      <div><span className="font-medium">Lateral Fee:</span> ₹{parseFloat(formData.lateral_fee || 0).toLocaleString('en-IN')}</div>
                    )}
                    {formData.has_caution_money && formData.caution_money && (
                      <div><span className="font-medium">Caution:</span> ₹{parseFloat(formData.caution_money).toLocaleString('en-IN')}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button type="submit" className="w-full" data-testid="submit-fee-button">
                {editMode ? 'Update Fee Structure' : 'Create Fee Structure'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <Input
              placeholder="Search by name or type..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-fees-input"
            />
          </div>
          
          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedCourseFilter}
              onChange={(e) => setSelectedCourseFilter(e.target.value)}
              data-testid="filter-course-select"
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name} ({course.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedTypeFilter}
              onChange={(e) => setSelectedTypeFilter(e.target.value)}
              data-testid="filter-type-select"
            >
              <option value="">All Fee Types</option>
              {feeTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Grouped Fee Structures */}
      <div className="space-y-6">
        {groupedFees.length === 0 ? (
          <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
            <DollarSign size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No fee structures found. Add your first fee structure to get started.</p>
          </div>
        ) : (
          groupedFees.map(({ type, fees, count, totalAmount }) => (
            <div key={type} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid={`fee-type-group-${type}`}>
              {/* Type Header */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-600 p-2 rounded-lg">
                      <DollarSign size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{type}</h2>
                      <p className="text-sm text-gray-600 mt-1">{count} fee structure(s)</p>
                    </div>
                  </div>
                  <div className="bg-white px-6 py-3 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-2xl font-bold text-green-600">₹{totalAmount.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>

              {/* Fees Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Regular Fee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lateral Fee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Caution Money</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {fees.map((fee) => {
                      const course = courses.find(c => c.id === fee.course_id);
                      return (
                      <tr key={fee.id} className="hover:bg-gray-50" data-testid={`fee-row-${fee.id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="bg-blue-100 p-2 rounded-lg">
                              <DollarSign size={18} className="text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-800">{getCourseName(fee.course_id)}</p>
                              <p className="text-xs text-gray-500">{getCourseCode(fee.course_id)}</p>
                              {course?.lateral_entry_allowed && (
                                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Lateral Entry Allowed
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{getSessionName(fee.session_id)}</td>
                        <td className="px-6 py-4">
                          <div className="bg-blue-50 px-3 py-1 rounded-full inline-block">
                            <span className="text-sm font-bold text-blue-700">₹{(fee.regular_fee || fee.total_fee)?.toLocaleString('en-IN') || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {course?.lateral_entry_allowed ? (
                            <div className="bg-orange-50 px-3 py-1 rounded-full inline-block">
                              <span className="text-sm font-bold text-orange-700">₹{(fee.lateral_fee || fee.total_fee)?.toLocaleString('en-IN') || 0}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {fee.has_caution_money ? (
                            <div className="bg-green-50 px-3 py-1 rounded-full inline-block">
                              <span className="text-sm font-bold text-green-700">₹{fee.caution_money?.toLocaleString('en-IN') || 0}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                            fee.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {fee.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleEdit(fee)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit"
                              data-testid={`edit-fee-${fee.id}`}
                            >
                              <Edit size={18} />
                            </button>
                            <button 
                              onClick={() => handleDelete(fee.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete"
                              data-testid={`delete-fee-${fee.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Total Fee Structures</p>
              <p className="text-3xl font-bold text-blue-700 mt-2" data-testid="total-fees-stat">{totalFees}</p>
            </div>
            <Filter className="text-blue-400" size={40} />
          </div>
        </div>
        
        <div className="bg-green-50 p-6 rounded-xl border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">Total Amount</p>
              <p className="text-3xl font-bold text-green-700 mt-2">₹{totalAmount.toLocaleString('en-IN')}</p>
            </div>
            <TrendingUp className="text-green-400" size={40} />
          </div>
        </div>
        
        <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Average Fee</p>
              <p className="text-3xl font-bold text-purple-700 mt-2">₹{Math.round(avgAmount).toLocaleString('en-IN')}</p>
            </div>
            <TrendingUp className="text-purple-400" size={40} />
          </div>
        </div>
        
        <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">Fee Types</p>
              <p className="text-3xl font-bold text-orange-700 mt-2">{groupedFees.length}</p>
            </div>
            <div className="text-orange-400 text-2xl font-bold">📋</div>
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
          <h3 className="font-semibold text-gray-800 mb-2">Course Coverage</h3>
          <p className="text-sm text-gray-600">
            Fee structures for <span className="font-bold text-blue-600">{uniqueCourses} courses</span>
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
          <h3 className="font-semibold text-gray-800 mb-2">Active Status</h3>
          <p className="text-sm text-gray-600">
            <span className="font-bold text-green-600">{feeStructures.filter(f => f.is_active).length}</span> active structures
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
          <h3 className="font-semibold text-gray-800 mb-2">Fee Range</h3>
          <p className="text-sm text-gray-600">
            {feeStructures.length > 0 ? (
              <>
                ₹{Math.min(...feeStructures.map(f => f.total_fee || 0)).toLocaleString('en-IN')} to{' '}
                ₹{Math.max(...feeStructures.map(f => f.total_fee || 0)).toLocaleString('en-IN')}
              </>
            ) : '₹0'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeeStructures;
